/**
 * useOptimisticDealSave - Optimistic Updates for Deal Saving
 *
 * Phase 6 of the Deals Flow Refactoring
 *
 * This hook provides optimistic update functionality by:
 * 1. Immediately applying changes to the UI before server confirmation
 * 2. Rolling back if the server request fails
 * 3. Reconciling with server response on success
 *
 * Integrates with:
 * - useDealEditingMachine (Phase 1) for state management
 * - useSaveDealOrchestrator (Phase 4) for save operations
 * - useAllItemsLive (Phase 3) for WebSocket reconciliation
 *
 * @see docs/DEALS_REFACTORING.md for full context
 */

import { SEARCH_DATA_KEY } from 'api/search'
import { useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSaveDealOrchestrator, type SaveProgress, type SaveResult } from './useSaveDealOrchestrator'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { dedupeAndNormalizeProducts } from 'context/DealsDetailsProvider/normalizeDeal'
import { calculateDealTotals } from 'utils/dealCalculations'
import type { NewDeal, Product, DealUpdate, ProductChanges } from 'types'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface OptimisticSnapshot {
  deal: NewDeal | null
  products: Product[]
  timestamp: number
}

export interface OptimisticSaveArgs {
  deal: NewDeal
  dealUpdates: DealUpdate[]
  currentProducts: Product[]
  productChanges: ProductChanges
  recipientsChanged?: boolean
  onReassignAuth?: () => Promise<any>
  onReassignClient?: () => Promise<any>
  onTimeline?: () => Promise<any>
  onPandaDocUpdate?: (_savedDeal: NewDeal, _savedProducts: Product[]) => Promise<void>
}

export interface OptimisticSaveResult extends SaveResult {
  /** Whether optimistic update was applied */
  optimisticApplied: boolean
  /** Whether rollback occurred */
  rolledBack: boolean
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const ALL_ITEMS_KEY = SEARCH_DATA_KEY

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useOptimisticDealSave() {
  const qc = useQueryClient()
  const orchestrator = useSaveDealOrchestrator()
  const snapshotRef = useRef<OptimisticSnapshot | null>(null)
  const { showSnackbar } = useSnackbarContext()

  /**
   * Apply optimistic update to React Query cache
   * Returns a rollback function
   */
  const applyOptimisticUpdate = useCallback(
    (deal: NewDeal, products: Product[], dealUpdates: DealUpdate[]) => {
      const dealPK = deal.PK
      const dealSK = deal.SK

      // Take snapshot for potential rollback
      const previousData = qc.getQueryData<any>(ALL_ITEMS_KEY)
      snapshotRef.current = {
        deal: previousData?.deals?.find((d: any) => d.PK === dealPK && d.SK === dealSK) ?? null,
        products: previousData?.products?.filter((p: any) => p.deal_uuid === deal.deal_uuid) ?? [],
        timestamp: Date.now(),
      }

      // Build optimistic deal with updates applied
      const optimisticDeal: NewDeal = { ...deal }
      for (const update of dealUpdates) {
        if (update.fieldName && update.newValue !== undefined) {
          ;(optimisticDeal as any)[update.fieldName] = update.newValue
        }
      }

      // Calculate totals with current products
      optimisticDeal.totals = calculateDealTotals(optimisticDeal, products)

      // Apply to cache optimistically
      qc.setQueryData(ALL_ITEMS_KEY, (prev: any) => {
        if (!prev) return prev
        const next = { ...prev }

        // Update deal
        if (Array.isArray(next.deals)) {
          next.deals = next.deals.map((d: any) =>
            d.PK === dealPK && d.SK === dealSK
              ? { ...d, ...optimisticDeal, hasProducts: products.length > 0 }
              : d
          )
        }

        // Update products (apply optimistic product state)
        if (Array.isArray(next.products)) {
          const otherProducts = next.products.filter((p: any) => p.deal_uuid !== deal.deal_uuid)
          next.products = dedupeAndNormalizeProducts([...otherProducts, ...products])
        }

        return next
      })

      // Also update item-specific cache
      qc.setQueryData(['item', dealPK, dealSK], (old: any) => ({
        ...(old ?? {}),
        ...optimisticDeal,
      }))

      // Return rollback function
      return () => {
        if (!snapshotRef.current) return

        qc.setQueryData(ALL_ITEMS_KEY, (prev: any) => {
          if (!prev) return prev
          const next = { ...prev }

          // Restore deal
          if (Array.isArray(next.deals) && snapshotRef.current?.deal) {
            next.deals = next.deals.map((d: any) =>
              d.PK === dealPK && d.SK === dealSK ? snapshotRef.current!.deal : d
            )
          }

          // Restore products
          if (Array.isArray(next.products) && snapshotRef.current?.products) {
            const otherProducts = next.products.filter((p: any) => p.deal_uuid !== deal.deal_uuid)
            next.products = dedupeAndNormalizeProducts([
              ...otherProducts,
              ...snapshotRef.current.products,
            ])
          }

          return next
        })

        // Restore item cache
        if (snapshotRef.current.deal) {
          qc.setQueryData(['item', dealPK, dealSK], snapshotRef.current.deal)
        }

        snapshotRef.current = null
      }
    },
    [qc]
  )

  /**
   * Save with optimistic updates
   *
   * 1. Immediately applies changes to cache
   * 2. Sends to server via orchestrator
   * 3. Reconciles or rolls back based on result
   */
  const saveOptimistic = useCallback(
    async (args: OptimisticSaveArgs): Promise<OptimisticSaveResult> => {
      const {
        deal,
        dealUpdates,
        currentProducts,
        productChanges,
        recipientsChanged,
        onReassignAuth,
        onReassignClient,
        onTimeline,
        onPandaDocUpdate,
      } = args

      const dealPK = deal.PK
      const dealSK = deal.SK
      const deal_uuid = deal.deal_uuid ?? deal.SK.replace('DEAL_', '')

      // Step 1: Apply optimistic update
      const rollback = applyOptimisticUpdate(deal, currentProducts, dealUpdates)
      let rolledBack = false

      try {
        // Step 2: Send to server
        const result = await orchestrator.save({
          deal,
          dealPK,
          dealSK,
          deal_uuid,
          dealUpdates,
          currentProducts,
          productChanges,
          recipientsChanged,
          onReassignAuth,
          onReassignClient,
          onTimeline,
          onPandaDocUpdate,
          silent: true, // We handle notifications ourselves
        })

        if (!result.ok) {
          // Step 3a: Rollback on error
          rollback()
          rolledBack = true
          // Show error notification to user - don't silently fail!
          const errorMessage = result.error?.message || 'Failed to save deal'
          showSnackbar(errorMessage, 'error')
          console.error('[useOptimisticDealSave] Save failed, rolled back:', result.error)
        }
        // Step 3b: On success, the orchestrator already updated cache with server response
        // The optimistic data will be overwritten with confirmed data

        snapshotRef.current = null

        return {
          ...result,
          optimisticApplied: true,
          rolledBack,
        }
      } catch (error) {
        // Unexpected error - rollback
        rollback()
        rolledBack = true
        snapshotRef.current = null

        // Show error notification to user
        const errorMessage = error instanceof Error ? error.message : 'Failed to save deal'
        showSnackbar(errorMessage, 'error')
        console.error('[useOptimisticDealSave] Unexpected error, rolled back:', error)

        return {
          ok: false,
          error: error instanceof Error ? error : new Error(String(error)),
          completedSteps: [],
          optimisticApplied: true,
          rolledBack: true,
        }
      }
    },
    [orchestrator, applyOptimisticUpdate, showSnackbar]
  )

  return {
    saveOptimistic,
    progress: orchestrator.progress,
    isSaving: orchestrator.isSaving,
    reset: orchestrator.reset,
    // Also expose non-optimistic save for cases where you don't want optimistic updates
    saveWithProgress: orchestrator.save,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Export types
// ─────────────────────────────────────────────────────────────────────────────

export type { SaveProgress, SaveResult }
